import { action, computed, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { defaultReportsFilters } from '../constants/reprots';
import { IReportsFilters, IReportsParams, REPORTS_BODY_KEY } from '../types/reports';
import { findAllSelectedOption, skipAllSelectedOption } from '../../OrdersRatingScreen/helpers';
import { ordersRatingStore } from '../../OrdersRatingScreen/store/ordersRatingStore';
import { dateFormatForRequest } from '~/constants/dateTime';
import { allOptionsValue, SORTING_DIRECTION } from '~/constants/main';
import { localeStore } from '~/mobx/localesStore';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import { appStore } from '~/mobx/appStore';
import client from '~/services/client';
import { isObjectsEqual } from '~/helpers';

export class ReportsStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return false;
  }

  @observable filters: IReportsFilters = defaultReportsFilters;

  @action setFilters = (filters: Partial<IReportsFilters>) => {
    this.filters = { ...this.filters, ...filters };
  };

  @action updateBranches = (currentOptions: IPickerOption[], selectedOptions: IPickerOption[]) => {
    if (this.filters.branches) {
      const updatedBranches = currentOptions.map((existingBranch: any) => {
        const updatedBranch = selectedOptions[0].data.find((newBranch: any) => newBranch.id === existingBranch.value);

        if (updatedBranch) {
          const isChanged = updatedBranch.description.branch_name !== existingBranch.label;

          if (isChanged) {
            return {
              ...existingBranch,
              label: updatedBranch.description.branch_name,
            };
          }
        }

        return existingBranch;
      });
      return updatedBranches;
    }
    return selectedOptions;
  };

  @action updateFilters = (branchOptions: IPickerOption[]) => {
    if (branchOptions.length && !isObjectsEqual(branchOptions, this.filters.franchisee, ['id'])) {
      if (branchOptions[0]?.data) {
        const branches = this.updateBranches(this.filters.branches, branchOptions);
        const franchisee_branches = this.updateBranches(this.filters.franchisee_branches, branchOptions);
        this.setFilters({
          branches,
          ...(appStore.isAddonsAvailable.franchise && { franchisee_branches }),
        });
      }
    }
  };

  @action selectSort = ({ sortField, sortDir }: { sortField: REPORTS_BODY_KEY | undefined; sortDir: SORTING_DIRECTION | undefined }) => {
    this.filters = { ...this.filters, sortField, sortDir };
  };

  @action selectAllBranchesInFirstInit = () => {
    if (!this.filters.branches?.length || (this.filters.branches.length === 1 && this.filters.branches[0].value === allOptionsValue)) {
      this.selectAllBranches();
    }
  };

  @action changeBranchSelection = (branches: IPickerOption[], branchType: 'franchisee_branches' | 'branches') => {
    if (branches) {
      const availableBranches = branchType === 'franchisee_branches' ? this.availableBranches : ordersRatingStore.availableBranches;
      const currentBranches = this.filters[branchType];
      const allBranchesOptionWasNotChanged = !!findAllSelectedOption(branches) === !!findAllSelectedOption(currentBranches);
      const allBranchesOptionWasSelected = !!findAllSelectedOption(branches) && !findAllSelectedOption(currentBranches);
      const allBranchesOptionWasUnselected = !findAllSelectedOption(branches) && !!findAllSelectedOption(currentBranches);
      const someOptionWasUnselected = allBranchesOptionWasNotChanged && branches.length !== currentBranches.length;
      const allAvailableOptionsWasSelected =
        allBranchesOptionWasNotChanged && !findAllSelectedOption(branches) && branches.length === availableBranches.length - 1;

      if (allBranchesOptionWasSelected || allAvailableOptionsWasSelected) {
        this.filters[branchType] = [...availableBranches];
      } else if (allBranchesOptionWasUnselected) {
        this.filters[branchType] = [];
      } else if (someOptionWasUnselected) {
        this.filters[branchType] = skipAllSelectedOption(branches);
      } else {
        this.filters[branchType] = branches;
      }
    }
  };

  @computed get availableBranches() {
    return this.filters.branches.length
      ? [{ label: localeStore.t('reports_select_all_branches_dropdown'), value: allOptionsValue }, ...this.filters.branches]
      : [];
  }

  @action selectAllFranchiseeBranches = () => {
    this.filters.franchisee_branches = this.availableBranches;
  };

  @action selectAllBranches = () => {
    if (!this.filters.franchisee?.[0]?.value) {
      this.filters.branches = ordersRatingStore.availableBranches;
    }
  };

  private getOptionsValues = (options: IPickerOption[]): number[] => {
    return options.filter((branch) => branch.value !== allOptionsValue).map((branch) => branch.value as number);
  };

  getReportBuildParams = (): IReportsParams => {
    const data: IReportsParams = {
      from: moment(this.filters.startDate * 1000).format(dateFormatForRequest),
      to: moment(this.filters.endDate * 1000).format(dateFormatForRequest),
      branches: appStore.isAddonsAvailable.franchise
        ? this.getOptionsValues(this.filters.franchisee_branches)
        : this.getOptionsValues(this.filters.branches),
    };

    if (this.filters.franchisee) data.franchisee_id = this.filters.franchisee[0].value;

    return data;
  };

  @action getPagination = async (page: number) => {
    try {
      return await client.get('/v3/franchises', {
        params: {
          page,
        },
      });
    } catch (error) {
      throw error;
    }
  };
}

export const reportsStore = new ReportsStore();
